import React, { useCallback, useMemo, useState } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useMainEntityName, useMainEntityType, useProductEntity, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import {
  useAccuracyFilterStore,
  AccuracyFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/AccuracyFilterStore';
import AccuracyFilterModal from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Modals/AccuracyModal';
import {
  CategoryType,
  ContentMetricType,
  GetVoiceAndToneCurrentAndTargetVectorsResponse,
  VoiceAndToneMatchType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { useContentChartSubtitles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useDynamicSubtitle';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { formatMetric, getItemHighlightsDisplayConfig } from 'src/utils/app';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { VoiceAndToneScatterPlot } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/VoiceAndTone/VoiceAndToneScatterPlot';
import { VoiceAndTonePagePopup } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/VoiceAndTone/VoiceAndTonePagePopup';
import { ProductLevelContentAccuracySummary } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ProductLevelContentAccuracySummary';
import { ContentSplineChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ContentSplineChart';
import { ContentDropdownBarChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ContentBarChart';
import {
  ContentSubTabs,
  createMetricColumnDefinition,
  createVoiceAndToneMatchAnalysisColumnDefinitions,
  creteProductColumWithSetWidth,
  voiceAndToneHeaderTextStyle
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils';
import { ProductLevelVoiceAndToneAnalysisChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ProductLevelVoiceAndToneAnalysisChart';
import { BarChartData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import {
  TableGridAdapter,
  VoiceAndToneScatterPlotAdapter
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/TableGridAdapter';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import AccuracyBarPercent from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/AccuracyBarPercent';
import { ContentTableGridV3 } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ContentTableGridV3';
import { useStacklineTheme } from '@stackline/ui';
import { ContentSectionId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import { PAGINATION_CONSTANTS } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/constants';
import {
  FilterLink,
  useFilterRendering
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useFilterRendering';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

enum VoiceAndToneColumnIndex {
  DominantVoice,
  DominantTone,
  VoiceMatchScore,
  ToneMatchScore,
  MatchType
}

const useVoiceToneMatchTypesFilter = () => {
  const filterParamsFromUrl = new URLSearchParams(window.location.search);
  const filters = JSON.parse(filterParamsFromUrl.get('filter'))?.matchTypes ?? [];
  return filters as VoiceAndToneMatchType[];
};

const contentAccuracyCollumnDefinitions = (showItemHighlights: boolean) => {
  const columns: [string, string][] = [
    ['contentAccuracyScore', 'Total Accuracy'],
    ['titleScore', 'Title Accuracy'],
    ...(showItemHighlights
      ? ([['itemHighlightsScore', 'Item Highlights Accuracy']] as [string, string][])
      : []),
    ['featureBulletsScore', 'Bullets Accuracy'],
    ['descriptionScore', 'Description Accuracy'],
    ['imagesScore', 'Images Accuracy'],
    ['videosScore', 'Videos Accuracy']
  ];

  return columns.map(([id, header], index, array) => {
    const showPercentageChange = id === 'contentAccuracyScore';
    if (index === array.length - 1) {
      return createMetricColumnDefinition([id, header], {
        cellStyle: { paddingRight: '16px' },
        showPercentChange: showPercentageChange
      });
    }

    return createMetricColumnDefinition([id, header], { showPercentChange: showPercentageChange });
  });
};

const mapFilters = (accuracyFilters: AccuracyFilters) => {
  return {
    filterTitleInaccuracies: accuracyFilters.titleInaccuracies,
    filterItemHighlightsInaccuracies: accuracyFilters.itemHighlightsInaccuracies,
    filterBulletPointsInaccuracies: accuracyFilters.bulletInaccuracies,
    filterImageInaccuracies: accuracyFilters.imageInaccuracies,
    filterDescriptionInaccuracies: accuracyFilters.descriptionInaccuracies,
    filterVideoInaccuracies: accuracyFilters.videoInaccuracies
  };
};

const metricOptions: SlDropdownMenuOption[] = [
  { id: ContentMetricType.ContentAccuracyScore, label: 'Content Accuracy' },
  { id: ContentMetricType.VoiceAndToneMatchAnalysis, label: 'Voice & Tone Accuracy' }
];

const ContentAccuracyV3Page = () => {
  const {
    getContentScoresByWeek,
    getContentScoresByProduct,
    getProductLevelVoiceAndToneAnalysis,
    getContentScoreByCategory,
    getProductLevelContentAccuracyDetails
  } = useContentService();
  const entityName = useMainEntityName();

  const { generateBarChartSubtitle } = useContentChartSubtitles();

  const title = 'Content Accuracy';
  const entityType = useMainEntityType();
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const { isProductEntity } = useProductEntity();
  const [showVoiceAndToneDefinitions, setShowVoiceAndToneDefinitions] = useState(false);
  const voiceToneMatchTypes = useVoiceToneMatchTypesFilter();
  const { openModal, filters, resetFilters, getActiveFilter } = useAccuracyFilterStore();
  const theme = useStacklineTheme();
  const { showItemHighlights } = getItemHighlightsDisplayConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeFilters = useMemo(() => getActiveFilter(), [getActiveFilter, filters]);
  const { renderFilters } = useFilterRendering(activeFilters);

  const [selectedMetricType, setSelectedMetricType] = useState<ContentMetricType>(
    ContentMetricType.ContentAccuracyScore
  );
  const [isLoading, setIsLoading] = useState(false);

  const getSubtitle = useCallback(() => {
    const tableType =
      selectedMetricType === ContentMetricType.ContentAccuracyScore ? 'content accuracy' : 'voice & tone accuracy';

    if (activeFilters.length > 0) {
      return (
        <Text variant="body1">
          The table displays the {tableType} scores for each product with low{' '}
          <span>{renderFilters(activeFilters)}</span> accuracy last week.{' '}
          <FilterLink clickable onClick={openModal}>
            Edit {activeFilters.length === 1 ? 'Filter' : `${activeFilters.length} Filters`}
          </FilterLink>
          .
        </Text>
      );
    }

    return `The table displays the ${tableType} scores for each product last week.`;
  }, [selectedMetricType, activeFilters, renderFilters, openModal]);

  const groupByOptionsByEntityType = {
    default: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ],
    brand: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      }
    ],
    category: [
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ],
    subcategory: [
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ]
  };

  const getVoiceAndToneColumnDefinitions = useCallback(() => {
    const baseVoiceAndToneColumns = createVoiceAndToneMatchAnalysisColumnDefinitions(theme);

    baseVoiceAndToneColumns[VoiceAndToneColumnIndex.VoiceMatchScore].header = (
      <Flex flexDirection="column">
        <Text {...voiceAndToneHeaderTextStyle}>Voice</Text>
        <Text {...voiceAndToneHeaderTextStyle}>Accuracy</Text>
      </Flex>
    );
    baseVoiceAndToneColumns[VoiceAndToneColumnIndex.ToneMatchScore].header = (
      <Flex flexDirection="column">
        <Text {...voiceAndToneHeaderTextStyle}>Tone</Text>
        <Text {...voiceAndToneHeaderTextStyle}>Accuracy</Text>
      </Flex>
    );

    baseVoiceAndToneColumns[VoiceAndToneColumnIndex.MatchType].header = (
      <Flex flexDirection="column">
        <Text {...voiceAndToneHeaderTextStyle}>Match</Text>
        <Text {...voiceAndToneHeaderTextStyle}>Type</Text>
      </Flex>
    );

    return baseVoiceAndToneColumns;
  }, [theme]);

  const getAccuracyScoresQueryFn = useCallback(
    (page?: number) => {
      return getContentScoresByProduct(
        ContentMetricType.ContentAccuracyScore,
        mainTimePeriod,
        comparisonTimePeriod,
        undefined,
        mapFilters(filters),
        page,
        PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE
      );
    },
    [getContentScoresByProduct, mainTimePeriod, comparisonTimePeriod, filters]
  );

  const getVoiceAndToneQueryFn = useCallback(
    (page?: number) => {
      return getContentScoresByProduct(
        ContentMetricType.VoiceAndToneMatchAnalysis,
        undefined,
        undefined,
        voiceToneMatchTypes,
        mapFilters(filters),
        page,
        PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE
      );
    },
    [getContentScoresByProduct, voiceToneMatchTypes, filters]
  );

  const getMetricTypeQueryFn = useCallback(
    (page: number) => {
      if (selectedMetricType === ContentMetricType.ContentAccuracyScore) {
        return getAccuracyScoresQueryFn(page);
      } else {
        return getVoiceAndToneQueryFn(page);
      }
    },
    [selectedMetricType, getAccuracyScoresQueryFn, getVoiceAndToneQueryFn]
  );

  const getActiveColumnDefinitions = useCallback(() => {
    return selectedMetricType === ContentMetricType.ContentAccuracyScore
      ? contentAccuracyCollumnDefinitions(showItemHighlights)
      : getVoiceAndToneColumnDefinitions();
  }, [selectedMetricType, getVoiceAndToneColumnDefinitions, showItemHighlights]);

  const handleMetricTypeChange = useCallback(
    (option: SlDropdownMenuOption) => {
      setSelectedMetricType(option.id as ContentMetricType);
      resetFilters();
    },
    [resetFilters]
  );

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: `${BEACON_CHART_SPACING}px` }}>
      <AccuracyBarPercent forceLoading={isLoading} />
      {isProductEntity && (
        <>
          <ProductLevelVoiceAndToneAnalysisChart
            title="Voice & Tone Accuracy"
            queryFn={() => getProductLevelVoiceAndToneAnalysis()}
            renderSubtitle={(data: GetVoiceAndToneCurrentAndTargetVectorsResponse) => {
              const { toneMatchScore, voiceMatchScore } = data;
              const formattedVoiceMatchScore = formatMetric(voiceMatchScore, METRICTYPE.PERCENT, {
                decimalPlaces: 1
              });
              const formattedToneMatchScore = formatMetric(toneMatchScore, METRICTYPE.PERCENT, {
                decimalPlaces: 1
              });
              return (
                <Text variant="body1">
                  This product had a voice accuracy of {formattedVoiceMatchScore} and a tone accuracy of{' '}
                  {formattedToneMatchScore} last week.{' '}
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => setShowVoiceAndToneDefinitions(true)}
                  >
                    View definitions
                  </span>{' '}
                  for voice and tone.
                </Text>
              );
            }}
          />

          <ProductLevelContentAccuracySummary
            queryFn={() => getProductLevelContentAccuracyDetails()}
            renderSubtitle={() => {
              return 'This section displays the content accuracy scores for every individual element last week.';
            }}
          />
        </>
      )}

      {!isProductEntity && (
        <>
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.BEACON_CONTENT_ACCURACY_SPLINE.name}
            chartName={CHART_NAMES.BEACON_CONTENT_ACCURACY_SPLINE_CHART}
          >
            <ContentSplineChart
              title={`${title} by Week`}
              queryFn={() =>
                getContentScoresByWeek({
                  contentMetricType: ContentMetricType.ContentAccuracyScore,
                  mainTimePeriod,
                  comparisonTimePeriod
                })
              }
              renderSubtitle={(score: string, change: number) => {
                const direction = change >= 0 ? 'higher' : 'lower';
                const absChange = formatMetric(Math.abs(change), METRICTYPE.PERCENT, {
                  decimalPlaces: 2,
                  showFullValue: true
                });
                const entityDisplay = isProductEntity ? 'this product' : entityName;
                return `On average, ${entityDisplay} earned a ${score} content accuracy score during the ${
                  mainTimePeriod.shortDisplayName
                } period, which is ${absChange} ${direction} compared to the ${comparisonTimePeriod.shortDisplayName.toLowerCase()}.`;
              }}
            />
          </ChartContextProvider>

          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.BEACON_CONTENT_ACCURACY_BAR.name}
            chartName={CHART_NAMES.BEACON_CONTENT_ACCURACY_BAR_CHART}
          >
            <ContentDropdownBarChart
              prefix={`${title} by`}
              options={groupByOptionsByEntityType[entityType] || groupByOptionsByEntityType.default}
              renderSubtitle={(selectedOption, data: BarChartData) => {
                return generateBarChartSubtitle({
                  metricName: 'content accuracy scores',
                  metricType: selectedOption
                });
              }}
            />
          </ChartContextProvider>

          <VoiceAndToneScatterPlot
            title="Voice & Tone Accuracy"
            renderTitle={() => {
              return (
                <Flex gap="sm" alignItems="center">
                  <Text variant="h4">Voice & Tone Accuracy</Text>
                </Flex>
              );
            }}
            queryFn={async () =>
              new VoiceAndToneScatterPlotAdapter(
                (await getContentScoresByProduct(
                  ContentMetricType.VoiceAndToneMatchAnalysis,
                  undefined,
                  undefined,
                  voiceToneMatchTypes,
                  mapFilters(filters)
                )) as TableGridAdapter
              )
            }
            renderSubtitle={() => {
              return (
                <Text variant="body1">
                  The matrix displays the voice and tone accuracy scores for each product last week.{' '}
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => setShowVoiceAndToneDefinitions(true)}
                  >
                    View definitions
                  </span>{' '}
                  for voice and tone.
                </Text>
              );
            }}
          />
          <div id={ContentSectionId.TableGrid} key={`${ContentSectionId.TableGrid}}`}>
            <ContentTableGridV3
              key={`metric-table-${selectedMetricType}-${JSON.stringify(filters)}`}
              title={
                <Flex alignItems="center" gap={8}>
                  <SlDropdownMenu
                    options={metricOptions}
                    selectedId={selectedMetricType}
                    onChange={handleMetricTypeChange}
                    buttonVariant="title"
                    menuStyles={{ minWidth: '180px' }}
                  />
                  <Text variant="h4">by Product</Text>
                </Flex>
              }
              productColumn={creteProductColumWithSetWidth('381px')}
              columnDefinitions={getActiveColumnDefinitions()}
              queryFn={(page: number) => getMetricTypeQueryFn(page)}
              mode="table"
              queryKeyOverride={`content-table-${selectedMetricType}-${JSON.stringify(filters)}`}
              subtitle={getSubtitle()}
              filterModal={<AccuracyFilterModal />}
              setOpen={openModal}
              disableFilterButton={selectedMetricType !== ContentMetricType.ContentAccuracyScore}
              loadingCallback={(isLoading) => {
                setIsLoading(isLoading);
              }}
              tableSx={{
                width: '1040px'
              }}
              isFilterActive={activeFilters.length > 0}
              pageSize={PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE}
              exportType={
                selectedMetricType === ContentMetricType.ContentAccuracyScore
                  ? ContentSubTabs.contentAccuracyV2
                  : ContentSubTabs.contentAccuracy
              }
            />
          </div>
        </>
      )}
      {showVoiceAndToneDefinitions && (
        <VoiceAndTonePagePopup
          open={showVoiceAndToneDefinitions}
          onClose={() => setShowVoiceAndToneDefinitions(false)}
        />
      )}
    </BasePageContainer>
  );
};

export default ContentAccuracyV3Page;
